import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { IconArrowLeft, IconArrowRight, IconLink } from "@tabler/icons-react";

const DataCardContainer = styled.div`
    color: white;
    background: linear-gradient(290deg, #482852 0%, #04132C 80%);
    box-shadow: 0 0 6px 6px rgba(0, 0, 0, 0.2);
`;

type KeywordFundingSourceProps = {
    min_amount: number;
    max_amount: number;
};

type Dimensions = {
    // Any keyname is allowed and the value is a number between 0 and 1
    [key: string]: number;
};


type FundingSourceProps = {
    keywordFields: KeywordFundingSourceProps;
};

const FilledBar = ({dimensionValue}: {dimensionValue:number}) => {
    let fitColor = "#00FF00";
    const height = 80;

    if(dimensionValue > .8){
        fitColor = "#C1FF72"
    }else if(dimensionValue > .6){
        fitColor = "#8EA14F"
    }else if(dimensionValue > .3){
        fitColor = "#737373"
    }else{
        fitColor = "#B35959"
    }


    return(
        <div className={`flex h-[80px] w-[15px] bg-[#545454] rounded-xl`}>
            <div className="w-[15px] self-end rounded-xl" style={{backgroundColor: fitColor, height: `${height*dimensionValue}px`}}>
            </div>
        </div>
    )

}

const Person: React.FC<any> = ({ data }) => {
        return (
        <div className="flex flex-col gap-2">
            <p>Skills: {data.skills}</p>
            <p>Country: {data.member_location_country}</p>
        </div>
    );
}


const FundingSource = ({ data }: {data:any}) => {
    return (
        <div className="flex text-xs flex-col gap-1">
            <div>Max Funding: {data.ai_max_funding_amount} in {data.ai_funding_amount_currency}</div>
            <div>Deadline: {data.latest_deadline}</div>
            <div>Locations: {data.ai_locations}</div>
            <div><a href={data.url} target="_blank"><IconLink /></a></div>
        </div>
    );
};

const Joke = ({ data }: {data:any}) => {
    return (
        <div className="flex flex-col gap-2">
            <div>{data.text}</div>
        </div>
    );
}

const TypeTransformation: { [key: string]: (e:any) => {description: string, title:string} } = {
    "Person": (data: any) => {
        return {
            title: `${data.member_name_first} ${data.member_name_last}`,
            description: data.member_job_description,
        };
    },
    "EUFundingSource": (data: any) => {
        return {
            title: data.title || data.topic_title || data.call_title,
            description: data.ai_short_description,
        }
    },
    "Joke": (data: any) => {
        return {
            title: "",
            description: "",
        }
    }
}

const DimensionContainer = styled.div`
`


const Dimensions: React.FC<{ dimensions: Dimensions }> = ({ dimensions }) => {
    return (
        <DimensionContainer className="flex h-[150px] min-h-[150px] flex-row gap-2 overflow-x-scroll">
            {Object.entries(dimensions).map(([key, value]) => (
                <div className="flex h-full flex-col gap-2 justify-center min-w-18 items-center">
                    <FilledBar dimensionValue={value} />
                    <p className="text-[10px] max-w-14 h-8 text-center overflow-hidden text-ellipsis">{key.replaceAll("_", " ")}</p>
                </div>
            ))}
        </DimensionContainer>
    );
}

interface ZaraProductProps {
    data: any;
    nextCard?: () => void;
    prevCard?: () => void;
    yAxis: string;
    xAxis: string;
  }
  

  const ZaraProduct: React.FC<ZaraProductProps> = ({ data, yAxis, xAxis, prevCard, nextCard }) => {
    const [current, setCurrent] = useState<number>(0);
    const [isAutoScroll, setIsAutoScroll] = useState<boolean>(true);
    const images: string[] = data.images || [];
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
    const goToNext = () => {
      setCurrent((prev) => (prev + 1) % images.length);
      setIsAutoScroll(false);
    };
  
    const goToPrev = () => {
      setCurrent((prev) => (prev - 1 + images.length) % images.length);
      setIsAutoScroll(false);
    };
  
    return (
      <div className="h-full w-full flex flex-col rounded-3xl overflow-hidden">
        {images.length > 0 && (
          <div className="relative h-full w-full rounded">
             <div className="absolute top-0 p-2 bg-slate-500/[.6] text-white flex flex-row justify-between">
                 <p className="text-sm">{xAxis}: {data[xAxis]}, {yAxis}: {data[yAxis]}</p>
                 {prevCard && <button
                onClick={prevCard}
                className=""
                aria-label="Next Card"
                >
                    <IconArrowLeft className="inline" />
                </button>}
                {nextCard && <button
                onClick={nextCard}
                className=""
                aria-label="Next Card"
                >
                   <IconArrowRight className="inline" />
                </button>}
                
            </div>
            <img
              src={images[current]}
              alt={`${data.name} ${current + 1}`}
              className="w-full h-full transition-transform duration-500"
            />
            {/* Navigation Buttons */}
            <button
              onClick={goToPrev}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-700 bg-opacity-50 text-white p-2 rounded-full focus:outline-none"
              aria-label="Previous Image"
            >
              &#8592;
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-700 bg-opacity-50 text-white p-2 rounded-full focus:outline-none"
              aria-label="Next Image"
            >
              &#8594;
            </button>
            <div className="text-lg bottom-[0px] flex flex-col p-2 bg-slate-500/[.6] absolute font-semibold text-white">
                <div>{data.name}</div>
                <div><a href={data.product_url} target="_blank"><IconLink /></a></div>
            </div>
          </div>
        )}
      </div>
    );
  };


const TypeToComponent: { [key: string]: React.FC<any> } = {
    "Person": Person,
    "EUFundingSource": FundingSource,
    "Joke": Joke,
    "ZaraProduct": ZaraProduct
};

  

const DataCard = ({data, dimensions, database, yAxis, xAxis, nextCard, prevCard}: {yAxis: string, xAxis: string, data:any, database: string, dimensions: string[], nextCard?: () => void, prevCard?: () => void}) => {
    const Component = TypeToComponent[database];
    if(database === "ZaraProduct") return <Component data={data} prevCard={prevCard} nextCard={nextCard} xAxis={xAxis} yAxis={yAxis} />;
    const normalizedDatum = TypeTransformation[database](data);
    const dimensionsValue = dimensions.reduce((acc, key) => {
        (acc as any)[key] = data[key];
        return acc;
    } , {} as Dimensions)
    return (
        <DataCardContainer className="h-full w-full flex p-2 flex-col rounded-xl overflow-hidden">
            <div className="w-full flex-row flex">
                <p className="text-sm">{xAxis}: {data[xAxis]}, {yAxis}: {data[yAxis]}</p>
                {nextCard && <div onClick = {nextCard}><IconArrowRight /></div>}
            </div>
            <div className="py-1">
                <p className="text-sm">{normalizedDatum.title}</p>
                <p className="overflow-y-scroll text-sm py-4 text-ellipsis w-full">{normalizedDatum.description}</p>
                <Component data={data} />
                <Dimensions dimensions = {dimensionsValue} />
            </div>
        </DataCardContainer>
    );
};

export default DataCard;
